import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Kbd,
  SearchInput,
  Spinner,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@crisp/crisp-ui/cabbage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faChevronRight,
  faFileLines,
  faMagnifyingGlass,
  faPenToSquare,
  faSparkles,
  faXmark,
} from '@crisp/crisp-ui/icons/classic/regular';
import { fetchSearchChunks, searchDocumentation } from '../../lib/api';
import { trackSearch } from '../../lib/analytics';
import { useCrispAuth } from '../../lib/auth0';
import { SearchChunk } from '../../types';
import { canAccess } from '../../lib/navAccess';
import { AssistantPanel } from './AssistantPanel';
import { DEFAULT_SITE, sitePathForSlug, type SiteConfig } from '../../lib/sites';
import { cn } from '../../lib/cn';
import styles from './SearchModal.module.css';

const RESULT_LIMIT = 15;
const KEYWORD_DEBOUNCE_MS = 80;
const SEMANTIC_DEBOUNCE_MS = 350;
/** Semantic results are only fetched when keyword search finds fewer hits than this. */
const SEMANTIC_BACKFILL_THRESHOLD = 3;

interface SearchModalProps {
  isOpen: boolean;
  initialMode?: 'keyword' | 'ai';
  initialQuery?: string;
  /**
   * The conversation outlives the modal so a reader can follow a citation and
   * come back to it. Changing this key starts a new one.
   */
  conversationKey?: number;
  currentPageSlug?: string;
  currentPageTitle?: string;
  site?: SiteConfig;
  onClose: () => void;
  onSelectResult?: (slug: string, anchor?: string) => void;
  /** Called when a citation takes the reader away from a live conversation. */
  onLeaveConversation?: () => void;
}

export const SearchModal: React.FC<SearchModalProps> = ({
  isOpen,
  initialMode,
  initialQuery,
  conversationKey = 0,
  currentPageSlug,
  currentPageTitle,
  site = DEFAULT_SITE,
  onClose,
  onSelectResult,
  onLeaveConversation,
}) => {
  const navigate = useNavigate();
  const { roles, isAuthenticated, isAdmin, getAccessToken } = useCrispAuth();
  const getAccessTokenRef = useRef(getAccessToken);
  getAccessTokenRef.current = getAccessToken;
  const rolesKey = (roles || []).join('|');

  const [mode, setMode] = useState<'keyword' | 'ai'>('keyword');
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [aiSeedQuery, setAiSeedQuery] = useState('');
  const [assistantResetKey, setAssistantResetKey] = useState(0);
  const [hasConversation, setHasConversation] = useState(false);

  const [allChunks, setAllChunks] = useState<SearchChunk[]>([]);
  const [filteredResults, setFilteredResults] = useState<SearchChunk[]>([]);
  const [isLoadingChunks, setIsLoadingChunks] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const resultsContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      const targetMode = initialMode || 'keyword';
      setMode(targetMode);
      setQuery(initialQuery || '');
      setAiSeedQuery('');
      setSelectedIndex(0);

      setIsLoadingChunks(true);
      (async () => {
        let token: string | null = null;
        if (getAccessTokenRef.current) {
          try {
            token = await getAccessTokenRef.current();
          } catch {
            token = null;
          }
        }
        return fetchSearchChunks(site.id, token);
      })()
        .then((chunks) => {
          setAllChunks(chunks);
        })
        .catch((err) => console.error('Failed to load search chunks:', err))
        .finally(() => setIsLoadingChunks(false));

      setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
    }
  }, [isOpen, initialMode, initialQuery, site.id]);
  const canAccessChunk = (chunk: SearchChunk): boolean =>
    canAccess(chunk.allowed_groups, { isAuthenticated, roles, isAdmin });

  useEffect(() => {
    if (!isOpen || mode !== 'keyword' || !query.trim()) {
      setFilteredResults((current) => (current.length === 0 ? current : []));
      setSelectedIndex((current) => (current === 0 ? current : 0));
      return;
    }

    const controller = new AbortController();
    const { signal } = controller;
    let semanticTimer: ReturnType<typeof setTimeout> | undefined;

    const localMatches = (): SearchChunk[] => {
      const q = query.toLowerCase();
      const words = q.split(/\s+/).filter(Boolean);
      return allChunks
        .filter((chunk) => {
          if (!canAccessChunk(chunk)) return false;

          const titleMatch = chunk.title.toLowerCase().includes(q);
          const headingMatch = chunk.heading?.toLowerCase().includes(q);
          const textMatch = words.every((w) => chunk.text.toLowerCase().includes(w));

          return titleMatch || headingMatch || textMatch;
        })
        .slice(0, RESULT_LIMIT);
    };

    const keywordTimer = setTimeout(async () => {
      let token: string | null = null;
      if (getAccessTokenRef.current) {
        try {
          token = await getAccessTokenRef.current();
        } catch {
          token = null;
        }
      }
      if (signal.aborted) return;

      let serverResults: SearchChunk[] | null = null;
      try {
        const resp = await searchDocumentation(query, RESULT_LIMIT, 'keyword', site.id, token, signal);
        serverResults = resp?.results ?? [];
      } catch {
        // Fall back to client-side filtering over allChunks
      }
      if (signal.aborted) return;

      // Substring matching also catches partially typed words that BM25 can't.
      const primary = serverResults && serverResults.length > 0 ? serverResults : localMatches();
      setFilteredResults(primary);
      setSelectedIndex(0);
      if (primary.length > 0) {
        trackSearch(query, primary.length, 'keyword');
      }

      if (serverResults === null || primary.length >= SEMANTIC_BACKFILL_THRESHOLD) return;

      semanticTimer = setTimeout(async () => {
        try {
          const resp = await searchDocumentation(query, RESULT_LIMIT, 'hybrid', site.id, token, signal);
          if (signal.aborted) return;
          const seen = new Set(primary.map((chunk) => chunk.id));
          const extra = (resp?.results ?? []).filter((chunk) => !seen.has(chunk.id));
          if (extra.length > 0) {
            setFilteredResults([...primary, ...extra].slice(0, RESULT_LIMIT));
          }
        } catch {
          // Keep the keyword results
        }
      }, SEMANTIC_DEBOUNCE_MS - KEYWORD_DEBOUNCE_MS);
    }, KEYWORD_DEBOUNCE_MS);

    return () => {
      controller.abort();
      clearTimeout(keywordTimer);
      clearTimeout(semanticTimer);
    };
  }, [isOpen, query, allChunks, mode, rolesKey, isAuthenticated, site.id]);

  useEffect(() => {
    if (mode === 'keyword' && resultsContainerRef.current) {
      const selectedEl = resultsContainerRef.current.querySelector(
        `[data-result-index="${selectedIndex}"]`
      );
      if (selectedEl) {
        (selectedEl as HTMLElement).scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex, mode]);

  const handleSelectResult = (chunk: SearchChunk) => {
    if (onSelectResult) {
      onSelectResult(chunk.slug, chunk.anchor);
    } else {
      const base = sitePathForSlug(site, chunk.slug);
      const target = chunk.anchor ? `${base}#${chunk.anchor}` : base;
      navigate(target);
    }
    onClose();
  };

  const handleNavigateCitation = (slug: string, anchor?: string) => {
    if (onSelectResult) {
      onSelectResult(slug, anchor);
    } else {
      const base = sitePathForSlug(site, slug);
      const target = anchor ? `${base}#${anchor}` : base;
      navigate(target);
    }
    onLeaveConversation?.();
    onClose();
  };

  const startNewChat = () => {
    setAiSeedQuery('');
    setAssistantResetKey((value) => value + 1);
  };

  const switchToAi = (seedFromQuery = false) => {
    setMode('ai');
    if (seedFromQuery && query.trim()) {
      setAiSeedQuery(query.trim());
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
      return;
    }

    if (e.key === 'Tab') {
      e.preventDefault();
      if (mode === 'keyword') {
        switchToAi(Boolean(query.trim()));
      } else {
        setMode('keyword');
        setTimeout(() => inputRef.current?.focus(), 0);
      }
      return;
    }

    if (mode !== 'keyword') return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) =>
        filteredResults.length > 0 ? (prev + 1) % filteredResults.length : 0
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) =>
        filteredResults.length > 0
          ? (prev - 1 + filteredResults.length) % filteredResults.length
          : 0
      );
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredResults[selectedIndex]) {
        handleSelectResult(filteredResults[selectedIndex]);
      }
    }
  };

  // Hidden rather than unmounted, so the Ask AI conversation and its scroll
  // position are still there when the reader comes back.
  return (
    <div
      aria-hidden={!isOpen}
      className={cn(styles.layer, isOpen && styles.layerOpen)}
    >
      <div onClick={onClose} className={styles.backdrop} />

      <div
        role="dialog"
        aria-modal="true"
        aria-label={mode === 'ai' ? 'Ask AI' : 'Search documentation'}
        onKeyDown={handleKeyDown}
        className={styles.dialog}
      >
        <div className={styles.toolbar}>
          <Tabs
            value={mode}
            onValueChange={(value) => {
              if (value === 'ai') switchToAi(Boolean(query.trim()));
              else setMode('keyword');
            }}
          >
            <TabsList variant="line">
              <TabsTrigger value="keyword">
                <FontAwesomeIcon icon={faMagnifyingGlass} />
                Search
              </TabsTrigger>
              <TabsTrigger value="ai">
                <FontAwesomeIcon icon={faSparkles} />
                Ask AI
              </TabsTrigger>
            </TabsList>
          </Tabs>

          <div className={styles.toolbarEnd}>
            {mode === 'ai' && hasConversation && (
              <Button
                variant="ghost"
                size="sm"
                onClick={startNewChat}
                iconPrimary={<FontAwesomeIcon icon={faPenToSquare} />}
              >
                New chat
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={onClose}
              aria-label="Close"
              iconPrimary={<FontAwesomeIcon icon={faXmark} />}
            />
          </div>
        </div>

        {mode === 'keyword' && (
          <div className={styles.searchRow}>
            <SearchInput
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onClear={() => setQuery('')}
              clearable={Boolean(query)}
              placeholder="Search pages, sections, keywords..."
            />
          </div>
        )}

        <div
          ref={resultsContainerRef}
          className={mode === 'keyword' ? styles.results : styles.resultsAi}
        >
          {mode === 'keyword' && (
            <>
              {isLoadingChunks ? (
                <div className={styles.empty}>
                  <Spinner />
                  <span>Loading search index...</span>
                </div>
              ) : query.trim() === '' ? (
                <div className={styles.empty}>
                  <p className={styles.emptyTitle}>Quick navigation</p>
                  <p>Type keywords to search across all documents and sections.</p>
                </div>
              ) : filteredResults.length === 0 ? (
                <div className={styles.empty}>
                  <p>No results found for "{query}"</p>
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => switchToAi(true)}
                    iconPrimary={<FontAwesomeIcon icon={faSparkles} />}
                  >
                    Ask AI instead
                  </Button>
                </div>
              ) : (
                <ul className={styles.list}>
                  {filteredResults.map((chunk, idx) => {
                    const isSelected = idx === selectedIndex;
                    return (
                      <li key={chunk.id}>
                        <button
                          type="button"
                          data-result-index={idx}
                          onClick={() => handleSelectResult(chunk)}
                          onMouseEnter={() => setSelectedIndex(idx)}
                          className={cn(styles.result, isSelected && styles.resultSelected)}
                        >
                          <FontAwesomeIcon icon={faFileLines} className="icon-slot" />
                          <div className={styles.resultBody}>
                            {chunk.breadcrumb && chunk.breadcrumb.length > 0 && (
                              <div className={styles.crumb}>{chunk.breadcrumb.join(' / ')}</div>
                            )}
                            <div className={styles.resultTitle}>
                              <span>{chunk.title}</span>
                              {chunk.heading && chunk.heading !== chunk.title && (
                                <>
                                  <FontAwesomeIcon icon={faChevronRight} className="icon-slot" />
                                  <span className={styles.heading}>{chunk.heading}</span>
                                </>
                              )}
                            </div>
                            <p className={styles.snippet}>{chunk.snippet || chunk.text}</p>
                          </div>
                          <FontAwesomeIcon
                            icon={faArrowRight}
                            className={`icon-slot ${styles.chevron}`}
                          />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </>
          )}
          <div hidden={mode !== 'ai'}>
            <AssistantPanel
              isActive={isOpen && mode === 'ai'}
              resetKey={`${site.id}:${conversationKey}:${assistantResetKey}`}
              initialQuery={initialMode === 'ai' ? initialQuery : undefined}
              seedQuery={aiSeedQuery}
              onSeedQueryConsumed={() => setAiSeedQuery('')}
              pageSlug={currentPageSlug}
              pageTitle={currentPageTitle}
              site={site}
              onNavigate={handleNavigateCitation}
              onConversationChange={setHasConversation}
            />
          </div>
        </div>

        <div className={styles.footer}>
          <div className={styles.hints}>
            {(mode === 'keyword'
              ? [
                  ['Tab', 'Ask AI'],
                  ['↑↓', 'navigate'],
                  ['↵', 'open'],
                ]
              : [
                  ['Tab', 'back to search'],
                  ['↵', 'send'],
                ]
            ).map(([key, label]) => (
              <span key={key} className={styles.hint}>
                <Kbd>{key}</Kbd>
                <span>{label}</span>
              </span>
            ))}
          </div>
          <span className={styles.hint}>
            <Kbd>ESC</Kbd>
            <span>close</span>
          </span>
        </div>
      </div>
    </div>
  );
};

export default SearchModal;
